import React, {
  useState,
  useContext,
  createContext,
  useRef,
  useEffect,
} from "react";
import {
  ChevronRight,
  ChevronDown,
  Plus,
  FileText,
  Hash,
  Type,
  List,
  Calculator,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Image,
  Link,
  Sigma,
  Check,
  X,
  Trash2,
  Settings,
  AlertTriangle,
  Edit3,
  Move,
} from "lucide-react";

import {
  updateExaminationTemplate,
  updateQuestion,
  deleteQuestion,
  deleteTemplateSection,
  getExaminationTemplateDetails, // ✅ ADD this import
} from "../../../services/Users-services.js";
import {
  ExamContext,
  generateMetadataFromExam,
  setInitialExamData,
  updateExaminationTemplateWithMetadata, // ✅ ADD this import
} from "./utility.js";
const TreeSidebar = ({ collapsed, onToggle }) => {
  const context = useContext(ExamContext);
  
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [hoveredNode, setHoveredNode] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);

  useEffect(() => {
    if (context && context.exam) {
      setExpandedNodes(
        new Set(["exam", ...context.exam.pages.map((p) => p.id)])
      );
    }
  }, [context]);

  // Auto-expand to show selected node
  useEffect(() => {
    if (context && context.selectedNode && context.exam) {
      const { selectedNode, exam } = context;
      const newExpanded = new Set(expandedNodes);

      if (selectedNode.type === "section") {
        // Find which page contains this section and expand it
        exam.pages.forEach((page) => {
          page.sections.forEach((section) => {
            if (section.id === selectedNode.id) {
              // Find the corresponding display page
              const displayPages = organizeContentForDisplay();
              displayPages.forEach((displayPage) => {
                displayPage.sections.forEach((displaySection) => {
                  if (displaySection.id === section.id) {
                    newExpanded.add(displayPage.id);
                  }
                });
              });
            }
          });
        });
      } else if (selectedNode.type === "question") {
        // Find which section and page contain this question and expand both
        exam.pages.forEach((page) => {
          page.sections.forEach((section) => {
            section.questions.forEach((question) => {
              if (question.id === selectedNode.id) {
                // Find the corresponding display page and expand both page and section
                const displayPages = organizeContentForDisplay();
                displayPages.forEach((displayPage) => {
                  displayPage.sections.forEach((displaySection) => {
                    if (displaySection.id === section.id) {
                      newExpanded.add(displayPage.id);
                      newExpanded.add(section.id);
                    }
                  });
                });
              }
            });
          });
        });
      }

      setExpandedNodes(newExpanded);
    }
  }, [context?.selectedNode, context?.exam]);

  if (!context) return null;

  const {
    exam,
    selectedNode,
    selectNode,
    clearSelection,
    updateExam,
    fetchExamDetails, // ✅ Make sure this is available from context
  } = context;


  const handleDeleteSection = async (sectionId) => {
    try {
      setDeleteModal(null);

      // Step 1: Delete section via API first
      const deleteResponse = await deleteTemplateSection(sectionId);

      if (
        deleteResponse.status === "success" ||
        deleteResponse.status === "200"
      ) {
        console.log("Section deleted successfully from API");

        // Step 2: Update local exam state to remove the deleted section
        const updatedExamData = {
          ...exam,
          pages: exam.pages.map((page) => ({
            ...page,
            sections: page.sections.filter(
              (section) => section.id !== sectionId
            ),
          })),
        };

        // Step 3: Update metadata using utility function
        const result = await updateExaminationTemplateWithMetadata(
          updatedExamData,
          exam.id,
          context.fetchExamDetails
        );

        if (result.success) {
          console.log("Section and metadata updated successfully");
          // Clear selection since the section is deleted
          clearSelection();
        } else {
          throw new Error(
            result.error || "Failed to update metadata after section deletion"
          );
        }
      } else {
        throw new Error(deleteResponse.message || "Failed to delete section");
      }
    } catch (error) {
      console.error("Error deleting section:", error);
      alert("Failed to delete section. Please try again.");
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    try {
      setDeleteModal(null);

      // Step 1: Delete question via API first
      const deleteResponse = await deleteQuestion(questionId);

      if (
        deleteResponse.status === "success" ||
        deleteResponse.status === "200"
      ) {
        console.log("Question deleted successfully from API");

        // Step 2: Update local exam state to remove the deleted question
        const updatedExamData = {
          ...exam,
          pages: exam.pages.map((page) => ({
            ...page,
            sections: page.sections.map((section) => ({
              ...section,
              questions: section.questions.filter(
                (question) => question.id !== questionId
              ),
            })),
          })),
        };

        // Step 3: Calculate new total marks
        const newTotalMarks = updatedExamData.pages.reduce(
          (total, page) =>
            total +
            page.sections.reduce(
              (sectionTotal, section) =>
                sectionTotal +
                section.questions.reduce(
                  (questionTotal, question) =>
                    questionTotal + (question.marks || 1),
                  0
                ),
              0
            ),
          0
        );

        // Step 4: Update exam data with new total marks
        updatedExamData.metadata = {
          ...updatedExamData.metadata,
          totalMarks: newTotalMarks,
        };
        updatedExamData.score = newTotalMarks;

        // Step 5: Update metadata using utility function
        const result = await updateExaminationTemplateWithMetadata(
          updatedExamData,
          exam.id,
          context.fetchExamDetails
        );

        if (result.success) {
          console.log("Question and metadata updated successfully");
          // Clear selection since the question is deleted
          clearSelection();
        } else {
          throw new Error(
            result.error || "Failed to update metadata after question deletion"
          );
        }
      } else {
        throw new Error(deleteResponse.message || "Failed to delete question");
      }
    } catch (error) {
      console.error("Error deleting question:", error);
      alert("Failed to delete question. Please try again.");
    }
  };

  const openDeleteModal = (type, id, name) => {
    setDeleteModal({ type, id, name });
  };

  const closeDeleteModal = () => {
    setDeleteModal(null);
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleNodeClick = (type, id, event) => {
    event.stopPropagation();
    if (selectedNode && selectedNode.type === type && selectedNode.id === id) {
      clearSelection();
    } else {
      selectNode(type, id);
    }
  };

  const isSelected = (type, id) => {
    return selectedNode && selectedNode.type === type && selectedNode.id === id;
  };

  // Function to organize content into logical pages for display
  const organizeContentForDisplay = () => {
    const allSections = exam.pages.flatMap((page) => page.sections);
    const sectionsPerPage = 2;

    let displayPages = [];
    let currentPageSections = [];
    let currentPageIndex = 0;

    allSections.forEach((section, index) => {
      currentPageSections.push(section);

      if (
        currentPageSections.length >= sectionsPerPage ||
        index === allSections.length - 1
      ) {
        displayPages.push({
          id: `display-page-${currentPageIndex}`,
          sections: currentPageSections,
          pageNumber: currentPageIndex + 1,
        });
        currentPageSections = [];
        currentPageIndex++;
      }
    });

    if (displayPages.length === 0) {
      displayPages.push({
        id: "display-page-0",
        sections: [],
        pageNumber: 1,
      });
    }

    return displayPages;
  };

  if (collapsed) {
    return (
      <div className="tree-sidebar collapsed">
        <button
          className="toggle-btn"
          onClick={onToggle}
          title="Expand sidebar"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    );
  }

  const displayPages = organizeContentForDisplay();

  return (
    <div className="tree-sidebar">
      <div className="tree-header">
        <div className="header-content">
          <FileText size={18} className="header-icon" />
          <h3>Structure</h3>
        </div>
        
      </div>

      <div className="tree-content">
        <div className="tree-node exam-node">
          <div className="node-content">
            <FileText size={16} className="node-icon" />
            <span className="exam-title">Question Paper</span>
          </div>
        </div>

        {displayPages.map((page, pageIndex) => {
          let globalQuestionIndex = 0;

          // Calculate global question index for this page
          for (let i = 0; i < pageIndex; i++) {
            globalQuestionIndex += displayPages[i].sections.reduce(
              (total, section) => total + section.questions.length,
              0
            );
          }

          return (
            <div key={page.id} className="tree-branch">
              <div className="tree-node page-node">
                <div className="node-content">
                  <button
                    className="expand-btn"
                    onClick={() => toggleNode(page.id)}
                    title={expandedNodes.has(page.id) ? "Collapse" : "Expand"}
                  >
                    {expandedNodes.has(page.id) ? (
                      <ChevronDown size={14} />
                    ) : (
                      <ChevronRight size={14} />
                    )}
                  </button>
                  <FileText size={16} className="node-icon" />
                  <span className="page-title">Page {page.pageNumber}</span>
                  <span className="question-count">
                    (
                    {page.sections.reduce(
                      (total, section) => total + section.questions.length,
                      0
                    )}{" "}
                    questions)
                  </span>
                </div>
              </div>

              {expandedNodes.has(page.id) && (
                <div className="tree-children">
                  {page.sections.map((section, sectionIndex) => {
                    const sectionQuestionStart = globalQuestionIndex;
                    globalQuestionIndex += section.questions.length;

                    return (
                      <div key={section.id} className="tree-branch">
                        <div
                          className={`tree-node section-node ${
                            isSelected("section", section.id) ? "selected" : ""
                          }`}
                          onClick={(e) =>
                            handleNodeClick("section", section.id, e)
                          }
                          onMouseEnter={() =>
                            setHoveredNode(`section-${section.id}`)
                          }
                          onMouseLeave={() => setHoveredNode(null)}
                        >
                          <div className="node-content">
                            <button
                              className="expand-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleNode(section.id);
                              }}
                              title={
                                expandedNodes.has(section.id)
                                  ? "Collapse"
                                  : "Expand"
                              }
                            >
                              {expandedNodes.has(section.id) ? (
                                <ChevronDown size={14} />
                              ) : (
                                <ChevronRight size={14} />
                              )}
                            </button>
                            <Hash size={16} className="node-icon" />
                            <span className="section-title">
                              {section.title}
                            </span>
                            <span className="question-count">
                              ({section.questions.length})
                            </span>
                          </div>

                          {(isSelected("section", section.id) ||
                            hoveredNode === `section-${section.id}`) && (
                            <div className="node-actions">
                              <button
                                className="action-btn danger"
                                title="Delete section"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeleteModal(
                                    "section",
                                    section.id,
                                    section.title
                                  );
                                }}
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          )}
                        </div>
                        
                        {expandedNodes.has(section.id) && (
                          <div className="tree-children">
                            {section.questions.map(
                              (question, questionIndex) => (
                                <div
                                  key={question.id}
                                  className={`tree-node question-node ${
                                    isSelected("question", question.id)
                                      ? "selected"
                                      : ""
                                  }`}
                                  onClick={(e) =>
                                    handleNodeClick("question", question.id, e)
                                  }
                                  onMouseEnter={() =>
                                    setHoveredNode(`question-${question.id}`)
                                  }
                                  onMouseLeave={() => setHoveredNode(null)}
                                >
                                  <div className="node-content">
                                    <div className="question-icon">
                                      {question.type === "with-options" ? (
                                        <List size={14} />
                                      ) : (
                                        <Type size={14} />
                                      )}
                                    </div>
                                    <span className="question-number">
                                      Q
                                      {sectionQuestionStart + questionIndex + 1}
                                    </span>
                                    <span className="question-preview">
                                      {question.richContent?.plainText ||
                                        question.content ||
                                        "Untitled question"}
                                    </span>
                                    <span className="marks-badge">
                                      {question.marks || 1}m
                                    </span>
                                  </div>

                                  {(isSelected("question", question.id) ||
                                    hoveredNode ===
                                      `question-${question.id}`) && (
                                    <div className="node-actions">
                                      <button
                                        className="action-btn danger"
                                        title="Delete question"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openDeleteModal(
                                            "question",
                                            question.id,
                                            question.richContent?.plainText ||
                                              question.content ||
                                              `Question ${
                                                sectionQuestionStart +
                                                questionIndex +
                                                1
                                              }`
                                          );
                                        }}
                                      >
                                        <Trash2 size={12} />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="tree-footer">
        <div className="tree-stats">
          <div className="stat-item">
            <span className="stat-label">Questions</span>
            <span className="stat-value">
              {exam.pages.reduce(
                (total, page) =>
                  total +
                  page.sections.reduce(
                    (sectionTotal, section) =>
                      sectionTotal + section.questions.length,
                    0
                  ),
                0
              )}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Sections</span>
            <span className="stat-value">
              {exam.pages.reduce(
                (total, page) => total + page.sections.length,
                0
              )}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Pages</span>
            <span className="stat-value">{displayPages.length}</span>
          </div>
        </div>

        {selectedNode && (
          <div className="selection-info">
            <div className="selection-badge">
              {selectedNode.type === "section" ? (
                <Hash size={12} />
              ) : (
                <Type size={12} />
              )}
              <span>
                {selectedNode.type === "section" ? "Section" : "Question"}{" "}
                selected
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Delete</h3>
              <button onClick={closeDeleteModal} className="modal-close-btn">
                <X size={16} />
              </button>
            </div>
            <div className="modal-body">
              <div className="delete-warning">
                <AlertTriangle size={48} className="warning-icon" />
                <p>Are you sure you want to delete this {deleteModal.type}?</p>
                <div className="item-name">
                  <strong>"{deleteModal.name}"</strong>
                </div>
                {deleteModal.type === "section" && (
                  <p className="warning-text">
                    This will also delete all questions in this section.
                  </p>
                )}
                <p className="warning-text">This action cannot be undone.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={closeDeleteModal} className="modal-cancel-btn">
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deleteModal.type === "section") {
                    handleDeleteSection(deleteModal.id);
                  } else {
                    handleDeleteQuestion(deleteModal.id);
                  }
                }}
                className="modal-delete-btn"
              >
                <Trash2 size={14} />
                Delete {deleteModal.type}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .tree-sidebar {
          width: 380px;
          min-width: 380px;
          background: #ffffff;
          border-right: 1px solid #e2e8f0;
          overflow-y: auto;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          position: relative;
          z-index: 50;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            sans-serif;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }

        .tree-sidebar.collapsed {
          width: 60px;
          min-width: 60px;
        }

        .tree-header {
          padding: 1rem;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f8fafc;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .header-icon {
          color: #3b82f6;
        }

        .tree-header h3 {
          margin: 0;
          font-size: 0.9rem;
          font-weight: 600;
          color: #1e293b;
          letter-spacing: -0.025em;
        }

        .toggle-btn {
          background: none;
          border: none;
          padding: 0.5rem;
          cursor: pointer;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          transition: all 0.2s ease;
        }

        .toggle-btn:hover {
          background: #e2e8f0;
          color: #374151;
        }

        .tree-content {
          padding: 0.75rem;
          flex: 1;
          overflow-y: auto;
        }

        .tree-footer {
          padding: 1rem;
          border-top: 1px solid #e2e8f0;
          background: #f8fafc;
        }

        .tree-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.5rem;
          margin-bottom: 0.75rem;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
          padding: 0.5rem;
          background: #ffffff;
          border-radius: 4px;
          border: 1px solid #e2e8f0;
        }

        .stat-label {
          color: #64748b;
          font-weight: 500;
          font-size: 0.75rem;
        }

        .stat-value {
          color: #1e293b;
          font-weight: 700;
          font-size: 1rem;
          color: #3b82f6;
        }

        .selection-info {
          border-top: 1px solid #e2e8f0;
          padding-top: 0.75rem;
        }

        .selection-badge {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: #dbeafe;
          color: #1e40af;
          border-radius: 4px;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .tree-node {
          display: flex;
          align-items: center;
          justify-content: between;
          padding: 0.5rem;
          cursor: pointer;
          border-radius: 4px;
          margin-bottom: 0.125rem;
          transition: all 0.2s ease;
          position: relative;
          border: 1px solid transparent;
        }

        .node-content {
          display: flex;
          align-items: center;
          flex: 1;
          min-width: 0;
        }

        .node-actions {
          display: flex;
          gap: 0.25rem;
          opacity: 0;
          animation: fadeIn 0.2s ease-out forwards;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateX(8px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .action-btn {
          background: #6b7280;
          border: 1px solid #6b7280;
          border-radius: 4px;
          padding: 0.25rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          color: white;
        }

        .action-btn:hover {
          background: #ef4444;
          border-color: #ef4444;
          transform: scale(1.05);
        }

        .tree-node:hover:not(.selected) {
          background: #f8fafc;
          border-color: #e2e8f0;
        }

        .tree-node.exam-node {
          cursor: default;
          background: #dbeafe;
          border: 1px solid #93c5fd;
          margin-bottom: 0.75rem;
          border-radius: 6px;
        }

        .tree-node.exam-node:hover {
          background: #dbeafe;
        }

        .exam-title {
          font-weight: 600;
          color: #1e40af;
          font-size: 0.9rem;
        }

        .tree-node.page-node {
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          margin: 0.5rem 0;
          font-weight: 500;
        }

        .page-title {
          color: #075985;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .question-count {
          margin-left: auto;
          font-size: 0.75rem;
          color: #64748b;
          background: #f1f5f9;
          padding: 0.125rem 0.375rem;
          border-radius: 12px;
        }

        .tree-node.selected {
          background: #dbeafe !important;
          border-color: #3b82f6 !important;
          font-weight: 500;
          box-shadow: 0 1px 3px rgba(59, 130, 246, 0.15);
        }

        .tree-node.section-node.selected {
          background: #dcfce7 !important;
          border-color: #22c55e !important;
        }

        .tree-node.question-node.selected {
          background: #fef3c7 !important;
          border-color: #f59e0b !important;
        }

        .section-title {
          font-size: 0.875rem;
          color: #22c55e;
          font-weight: 500;
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .question-number {
          font-size: 0.75rem;
          color: #f59e0b;
          font-weight: 600;
          min-width: 2rem;
        }

        .question-preview {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 0.75rem;
          color: #64748b;
          margin-left: 0.5rem;
        }

        .marks-badge {
          background: #fef3c7;
          color: #d97706;
          font-size: 0.675rem;
          padding: 0.125rem 0.375rem;
          border-radius: 12px;
          font-weight: 600;
          margin-left: 0.5rem;
        }

        .tree-node span {
          margin-left: 0.5rem;
          font-size: 0.875rem;
        }

        .node-icon {
          color: #64748b;
          margin-right: 0.5rem;
        }

        .expand-btn {
          background: none;
          border: none;
          padding: 0.25rem;
          margin-right: 0.25rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          color: #64748b;
          transition: all 0.2s ease;
        }

        .expand-btn:hover {
          background: rgba(0, 0, 0, 0.05);
          color: #374151;
        }

        .tree-children {
          margin-left: 1rem;
          border-left: 1px solid #f1f5f9;
          padding-left: 0.75rem;
          margin-top: 0.25rem;
          margin-bottom: 0.5rem;
        }

        .question-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          margin-right: 0.5rem;
        }

        /* Delete Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(4px);
          animation: fadeInModal 0.2s ease-out;
        }

        @keyframes fadeInModal {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .modal-content {
          background: #ffffff;
          border-radius: 8px;
          min-width: 400px;
          max-width: 500px;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 10px 10px -5px rgba(0, 0, 0, 0.04);
          border: 1px solid #e2e8f0;
          animation: slideInModal 0.3s ease-out;
        }

        @keyframes slideInModal {
          from {
            opacity: 0;
            transform: translateY(-16px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 2rem;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
          border-radius: 8px 8px 0 0;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 1.125rem;
          font-weight: 600;
          color: #1e293b;
        }

        .modal-close-btn {
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 6px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .modal-close-btn:hover {
          background: #f1f5f9;
          color: #374151;
        }

        .modal-body {
          padding: 2rem;
        }

        .delete-warning {
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }

        .warning-icon {
          color: #f59e0b;
          background: #fef3c7;
          padding: 0.75rem;
          border-radius: 50%;
        }

        .delete-warning p {
          margin: 0;
          font-size: 1rem;
          color: #374151;
          line-height: 1.5;
        }

        .item-name {
          padding: 0.75rem 1rem;
          background: #f8fafc;
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          font-family: monospace;
          color: #1e293b;
          max-width: 100%;
          word-break: break-word;
        }

        .warning-text {
          color: #dc2626 !important;
          font-weight: 500;
          font-size: 0.875rem !important;
        }

        .modal-footer {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
          padding: 1.5rem 2rem;
          border-top: 1px solid #e2e8f0;
          background: #f8fafc;
          border-radius: 0 0 8px 8px;
        }

        .modal-cancel-btn {
          padding: 0.75rem 1.5rem;
          background: #ffffff;
          color: #374151;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          font-size: 0.9rem;
          transition: all 0.2s ease;
        }

        .modal-cancel-btn:hover {
          background: #f9fafb;
          border-color: #9ca3af;
        }

        .modal-delete-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          font-size: 0.9rem;
          transition: all 0.2s ease;
          box-shadow: 0 1px 3px rgba(239, 68, 68, 0.3);
        }

        .modal-delete-btn:hover {
          background: #dc2626;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
        }

        /* Scrollbar styling */
        .tree-sidebar::-webkit-scrollbar {
          width: 4px;
        }

        .tree-sidebar::-webkit-scrollbar-track {
          background: #f8fafc;
        }

        .tree-sidebar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 2px;
        }

        .tree-sidebar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        .tree-content::-webkit-scrollbar {
          width: 4px;
        }

        .tree-content::-webkit-scrollbar-track {
          background: transparent;
        }

        .tree-content::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 2px;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .tree-sidebar {
            width: 100%;
            min-width: 100%;
            max-height: 300px;
          }

          .modal-content {
            min-width: 300px;
            max-width: 90vw;
            margin: 1rem;
          }

          .modal-header,
          .modal-body,
          .modal-footer {
            padding: 1rem 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default TreeSidebar;
